import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  List,
  Drawer,
  Typography,
  Box,
  Collapse,
  Tooltip,
  Divider,
  // Button,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { observer } from 'mobx-react-lite';
import CloseIcon from '@mui/icons-material/Close';

import { useStore } from 'logic';
import { RouteNames } from 'logic/routes';
import { designTokens } from 'theme';
import { SessionPermissions } from '@creative-kit/shared';
import type { Session } from 'logic/session';
import { AvatarMenu } from './AvatarMenu';
import { SessionName } from './SessionName';
import { SessionItem } from './SessionItem';
import { DuplicateSession } from './DuplicateSession';
import { ShareSession } from './ShareSession';
import { DuplicationRequestManagement } from './DuplicationRequestManagement';
import { ProjectDrawerView } from './ProjectDrawerView';
import { ResizableDrawerContent } from './ResizableDrawerContent';
import { ProjectTabs } from './ProjectTabs';
import { useProjectTab } from 'ui/ProjectTabContext';
import { getApplicationBarTitleLayout } from './applicationBarTitleLayout';

interface StyleProps {
  isArchiveExpanded: boolean;
  isTrashExpanded: boolean;
}
const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    backgroundColor: 'transparent',
    transition: theme.transitions.create('background-color'),
    zIndex: 1100, // Ensure it's above ImportView Container (z-index: 0)
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(15, 15, 15, 0.8)' : 'rgba(250, 250, 250, 0.8)',
    },
  },
  drawer: {
    width: 500,
  },
  expand: {
    marginLeft: 'auto',
    transform: (p: StyleProps) => (p.isArchiveExpanded ? 'rotate(180deg)' : 'rotate(0deg)'),
    transition: theme.transitions.create('transform'),
  },
  expandTrash: {
    marginLeft: 'auto',
    transform: (p: StyleProps) => (p.isTrashExpanded ? 'rotate(180deg)' : 'rotate(0deg)'),
    transition: theme.transitions.create('transform'),
  },
  importStep: {
    color: theme.palette.primary.light,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 24,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const ShareStatusButton = observer(({ session }: { session: Session }) => (
  <Tooltip
    title={
      session.data.publicAccessLevel === SessionPermissions.VIEW
        ? 'Anyone can VIEW this project (requires login)'
        : session.data.publicAccessLevel === SessionPermissions.EDIT
        ? 'Anyone can EDIT this project (requires login)'
        : 'Only you can View + Edit this project'
    }
  >
    <IconButton
      size="small"
      color="inherit"
      onClick={() => window.dispatchEvent(new CustomEvent('openShareDialog'))}
    >
      {session.data.publicAccessLevel === SessionPermissions.VIEW ? (
        <VisibilityIcon fontSize="small" />
      ) : session.data.publicAccessLevel === SessionPermissions.EDIT ? (
        <GroupIcon fontSize="small" />
      ) : (
        <LockIcon fontSize="small" />
      )}
    </IconButton>
  </Tooltip>
));

export const ApplicationBar = observer(() => {
  const [duplicateSessionId, setDuplicateSessionId] = useState<string>();
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isArchiveExpanded, setIsArchiveExpanded] = useState(false);
  const [isTrashExpanded, setIsTrashExpanded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showProjectView, setShowProjectView] = useState(false);
  const { activeTab: activeProjectTab, setActiveTab: setActiveProjectTab } = useProjectTab();
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const leftSectionRef = useRef<HTMLDivElement | null>(null);
  const rightSectionRef = useRef<HTMLDivElement | null>(null);
  const [titleLayout, setTitleLayout] = useState(() => ({
    shouldShowTitle: true,
    titleCenterX: 0,
    titleMaxWidth: 520,
  }));

  const handleExpandClick = () => setIsArchiveExpanded(!isArchiveExpanded);
  const handleExpandTrashClick = () => setIsTrashExpanded(!isTrashExpanded);

  const styles = useStyles({ isArchiveExpanded, isTrashExpanded });

  const store = useStore();
  const sessionsStore = store?.sessionsStore;
  const authStore = store?.authStore;
  const uiPermissions = store?.uiPermissions;
  const uiDuplicationRequestStore = store?.uiDuplicationRequestStore;
  const routerStore = store?.routerStore;

  if (!sessionsStore || !authStore || !routerStore) {
    return null;
  }

  const {
    activeSessions,
    archivedSessions,
    deletedSessions,
    currentSession,
    archiveSession,
    deleteSession,
  } = sessionsStore;
  const { user } = authStore;
  const { isCurrentSessionOwner, canEditCurrentSession, isGuestUser } = uiPermissions ?? {};

  const [showDuplicationRequests, setShowDuplicationRequests] = useState(false);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((isOpen) => {
      if (!isOpen) {
        // When opening drawer, navigate directly to All Projects view
        setShowProjectView(false);
      }

      return !isOpen;
    });
  }, []);

  // Don't auto-load duplication requests - only load when user opens the management UI
  // This prevents permission errors on every session load

  const duplicateSession = (sessionId: string) => {
    setDuplicateSessionId(sessionId);
    setDrawerOpen(false);
  };

  const loadSession = useCallback(
    (targetSessionId: string) => () => {
      setShowProjectView(true);
      setDrawerOpen(false);
      routerStore.goTo(RouteNames.SESSION_SEARCH, {
        params: { sessionId: targetSessionId },
      });
    },
    [routerStore],
  );

  const handleBackToProjects = () => {
    setShowProjectView(false);
  };

  // Add ESC key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && drawerOpen) {
        setDrawerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  useLayoutEffect(() => {
    if (!currentSession) {
      return undefined;
    }

    const toolbarEl = toolbarRef.current;
    const leftEl = leftSectionRef.current;
    const rightEl = rightSectionRef.current;

    if (!toolbarEl || !leftEl || !rightEl) {
      return undefined;
    }

    let frameId: number | null = null;

    const updateLayout = () => {
      frameId = null;

      const toolbarRect = toolbarEl.getBoundingClientRect();
      const leftRect = leftEl.getBoundingClientRect();
      const rightRect = rightEl.getBoundingClientRect();

      const nextLayout = getApplicationBarTitleLayout({
        gapStart: leftRect.right - toolbarRect.left,
        gapEnd: rightRect.left - toolbarRect.left,
      });

      setTitleLayout((prevLayout) =>
        prevLayout.shouldShowTitle === nextLayout.shouldShowTitle &&
        prevLayout.titleCenterX === nextLayout.titleCenterX &&
        prevLayout.titleMaxWidth === nextLayout.titleMaxWidth
          ? prevLayout
          : nextLayout,
      );
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(updateLayout);
    };

    scheduleUpdate();

    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });

    resizeObserver.observe(toolbarEl);
    resizeObserver.observe(leftEl);
    resizeObserver.observe(rightEl);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      resizeObserver.disconnect();
    };
  }, [
    currentSession,
    activeProjectTab,
    canEditCurrentSession,
    isCurrentSessionOwner,
    isGuestUser,
    uiDuplicationRequestStore.pendingRequests.length,
  ]);

  if (!user) {
    return null;
  }

  const renderProjectsList = () => (
    <List
      component="nav"
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        p: 0,
        '& .MuiListItem-root': {
          pr: 6,
          width: '100%',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
    >
      <Box
        m={1}
        display="flex"
        alignItems="center"
        sx={{
          cursor: 'pointer',
          width: '100%',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIsActiveExpanded(!isActiveExpanded);
          }}
          sx={{ mr: 1 }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isActiveExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>
        <Typography variant="h6" color="textSecondary">
          Active Projects
          <Typography
            component="sup"
            variant="subtitle1"
            sx={{
              ml: 0.5,
              color: 'text.secondary',
              verticalAlign: 'super',
              fontSize: '0.7em',
            }}
          >
            {activeSessions.length}
          </Typography>
        </Typography>
      </Box>

      <Collapse in={isActiveExpanded} timeout="auto">
        <List
          component="div"
          disablePadding
          sx={{
            width: '100%',
            '& .MuiListItem-root': {
              width: '100%',
            },
          }}
        >
          {activeSessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              onClick={loadSession(session.id)}
              archiveSession={archiveSession}
              deleteSession={deleteSession}
              duplicateSession={duplicateSession}
              isCurrentSession={currentSession?.id === session.id}
            />
          ))}
        </List>
      </Collapse>

      <Box
        m={1}
        display="flex"
        alignItems="center"
        sx={{
          cursor: 'pointer',
          width: '100%',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleExpandClick();
          }}
          sx={{ mr: 1 }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isArchiveExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>
        <Typography variant="h6" color="textSecondary">
          Archived Projects
          <Typography
            component="sup"
            variant="subtitle1"
            sx={{
              ml: 0.5,
              color: 'text.secondary',
              verticalAlign: 'super',
              fontSize: '0.7em',
            }}
          >
            {archivedSessions.length}
          </Typography>
        </Typography>
      </Box>

      <Collapse in={isArchiveExpanded} timeout="auto">
        <List
          component="div"
          disablePadding
          sx={{
            width: '100%',
            '& .MuiListItem-root': {
              width: '100%',
            },
          }}
        >
          {archivedSessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              onClick={loadSession(session.id)}
              archiveSession={archiveSession}
              deleteSession={deleteSession}
              duplicateSession={duplicateSession}
              isCurrentSession={currentSession?.id === session.id}
            />
          ))}
        </List>
      </Collapse>

      <Box
        m={1}
        display="flex"
        alignItems="center"
        onClick={handleExpandTrashClick}
        sx={{
          cursor: 'pointer',
          width: '100%',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleExpandTrashClick();
          }}
          sx={{ mr: 1 }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isTrashExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>
        <Typography variant="h6" color="textSecondary">
          Trashed Projects
          <Typography
            component="sup"
            variant="subtitle1"
            sx={{
              ml: 0.5,
              color: 'text.secondary',
              verticalAlign: 'super',
              fontSize: '0.7em',
            }}
          >
            {deletedSessions.length}
          </Typography>
        </Typography>
      </Box>

      <Collapse in={isTrashExpanded}>
        <List
          component="div"
          disablePadding
          sx={{
            width: '100%',
            '& .MuiListItem-root': {
              width: '100%',
            },
          }}
        >
          {deletedSessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              onClick={loadSession(session.id)}
              archiveSession={archiveSession}
              deleteSession={deleteSession}
              duplicateSession={duplicateSession}
              isCurrentSession={currentSession?.id === session.id}
            />
          ))}
        </List>
      </Collapse>
    </List>
  );

  return (
    <>
      <AppBar className={styles.root} position="static" enableColorOnDark color="default">
        <Toolbar
          ref={toolbarRef}
          variant="dense"
          sx={{
            minHeight: 48,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Left section: CQ Logo + Tabs */}
          <Box
            ref={leftSectionRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifySelf: 'start',
              minWidth: 0,
            }}
          >
            {/* CQ Logo - links to projects */}
            <Box
              component="button"
              onClick={() => routerStore.goTo(RouteNames.PROJECTS)}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: '-1px',
                color: designTokens.colors.text.primary,
                fontFamily: designTokens.typography.fontMono,
                padding: '4px 8px',
                borderRadius: designTokens.spacing.radiusSm,
                transition: `all ${designTokens.transitions.normal}`,
                '&:hover': {
                  bgcolor: designTokens.colors.bg.cardHover,
                },
              }}
            >
              CQ
            </Box>
            {/* Tabs - next to logo */}
            {currentSession && (
              <ProjectTabs activeTab={activeProjectTab} onTabChange={setActiveProjectTab} />
            )}
          </Box>
          {/* Middle section: Session name - absolutely positioned for TRUE center */}
          <Box
            sx={{
              position: 'absolute',
              left: `${titleLayout.titleCenterX}px`,
              transform: 'translateX(-50%)',
              display: titleLayout.shouldShowTitle ? 'flex' : 'none',
              alignItems: 'center',
              minWidth: 0,
              overflow: 'hidden',
              width: `${titleLayout.titleMaxWidth}px`,
              pointerEvents: 'auto',
            }}
          >
            {currentSession && <SessionName />}
          </Box>
          {/* Right section: controls */}
          <Box
            ref={rightSectionRef}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifySelf: 'end' }}
          >
            {currentSession && (
              <>
                {/* Duplication requests button - only show if user owns any sessions */}
                {uiDuplicationRequestStore.pendingRequests.length > 0 && (
                  <Tooltip
                    title={`${
                      uiDuplicationRequestStore.pendingRequests.length
                    } pending duplication request${
                      uiDuplicationRequestStore.pendingRequests.length > 1 ? 's' : ''
                    }`}
                  >
                    <IconButton
                      onClick={() => {
                        setShowDuplicationRequests(true);
                        // Requests are already loaded via real-time listener, but refresh to be sure
                      }}
                      size="small"
                      sx={{
                        position: 'relative',
                        color: designTokens.colors.text.secondary,
                        '&:hover': {
                          color: designTokens.colors.text.primary,
                        },
                      }}
                    >
                      <NotificationsIcon fontSize="small" />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 8,
                          height: 8,
                          bgcolor: '#ef4444',
                          borderRadius: '50%',
                          border: `2px solid ${designTokens.colors.bg.elevated}`,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {isCurrentSessionOwner === false && !isGuestUser && canEditCurrentSession && (
                  <Tooltip title="Duplicate Project To Your Account">
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => duplicateSession(currentSession.id)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <ShareStatusButton session={currentSession} />
                <ShareSession />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 1, height: 24, alignSelf: 'center' }}
                />
              </>
            )}
            <AvatarMenu />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            bgcolor: 'background.default',
            border: 'none',
            overflow: 'visible', // Allow resize handle to overflow
          },
        }}
        ModalProps={{
          BackdropProps: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        {showProjectView ? (
          <ProjectDrawerView
            onBackToProjects={handleBackToProjects}
            onClose={() => setDrawerOpen(false)}
            initialTab={0}
          />
        ) : (
          <ResizableDrawerContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    routerStore.goTo(RouteNames.HOME);
                    setDrawerOpen(false);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <HomeRoundedIcon fontSize="small" />
                </IconButton>
                <Typography variant="h1" sx={{ fontWeight: 500, fontSize: '2rem' }}>
                  Projects
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{renderProjectsList()}</Box>
          </ResizableDrawerContent>
        )}
      </Drawer>
      {duplicateSessionId && (
        <DuplicateSession
          open={duplicateSessionId !== undefined}
          onClose={() => setDuplicateSessionId(undefined)}
          sessionId={duplicateSessionId}
        />
      )}
      <DuplicationRequestManagement
        open={showDuplicationRequests}
        onClose={() => setShowDuplicationRequests(false)}
      />
    </>
  );
});
